import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import {
	FormControl,
	Grid,
	IconButton,
	Select,
	Table,
	MenuItem,
	TableBody,
	TableContainer,
	TableHead,
	TableSortLabel,
	TextField,
	Paper,
	checkboxClasses,
	Checkbox,
	TableRow,
} from '@mui/material';

import React, { useEffect, useRef } from 'react';

import styled from '@emotion/styled';
import { debounce } from 'lodash';

import { nextPaginationIcon, prevPaginationIcon } from '../../assets/images';
import { TABLE_CONSTANTS, TABLE_VIEW_PAGE_SIZE } from '../../constants';
import { ImgTagComponent, sortIcon } from '../../utils/utilFunctions';

import { Loader } from '../Loader/Loader';

import CellComponent from './CellComponent';
import {
	Column,
	Order,
	ICommonTableOneProps,
	StyledTableCell,
	StyledTableRow,
	tableThemes,
	onPageTextChanged,
} from './CommonTableStyles';
import TableRowComponent from './TabelRow';

import styles from './CommonTable.module.scss';

const { TABLE_SIZE_10, TABLE_SIZE_25, TABLE_SIZE_50 } = TABLE_VIEW_PAGE_SIZE;

const TableComponent = (props: ICommonTableOneProps) => {
	const {
		rows,
		columns,
		defaultSortKey,
		defaultSortDirection,
		totalItems,
		tableStyles,
		isCheckBoxEnabled,
		onRowChecked,
		clearSelection,
		onRowSelectable,
		onPageHandler,
		isLoading,
		onSortHandler,
		rowKey = 'id',
		rowHoverEnabled = false,
		customErrorMessage,
		enableHeaderColor,
		enableInnerTableRow,
		enableInnerTableTemplate,
		defaultQuery,
		colSpan,
		isRenderPagination,
		hideSelectPageOption,
		isFlatStyle,
	} = props;

	const queryPageNum = defaultQuery?.pageNumber || 0;
	const queryPageSize =
		defaultQuery?.pageSize || TABLE_CONSTANTS.customPageSize;
	const [order, setOrder] = React.useState<Order>(
		defaultSortDirection || 'asc',
	);
	const [orderBy, setOrderBy] = React.useState<keyof any>(defaultSortKey || '');
	const [page, setPage] = React.useState(queryPageNum);
	const [rowsPerPage, setRowsPerPage] = React.useState(queryPageSize);
	const [textFieldPage, setTextFieldPage] = React.useState<any>(
		queryPageNum + 1,
	);
	const [selectedIds, setSelectedIds] = React.useState<any>([]);
	const [tableData, setTableData] = React.useState<any>([]);
	const [rowHoverData, setRowHoverData] = React.useState<any>();
	const inputRef = useRef<HTMLInputElement>(null);
	const tablePadding = isFlatStyle
		? {}
		: { paddingTop: '8px', paddingLeft: '16px', paddingRight: '15px' };

	useEffect(() => {
		if (defaultQuery?.pageNumber === 0) {
			setTextFieldPage(1);
		}
		setPage(queryPageNum);
		setRowsPerPage(queryPageSize);
	}, [defaultQuery]);

	const maxHeight = '27.5em';
	const minWidth = '46.875em';

	const { checkboxSelectionColor, sortIconColor } = styles;

	const isSelected: any = (id: any) => selectedIds.indexOf(id) !== -1;
	const allSelected = rows?.every((obj: any) =>
		selectedIds.includes(obj[rowKey]),
	);
	const tableHeaderColumColor = enableHeaderColor
		? tableThemes[0].headColor
		: tableThemes[1].headColor;

	const TableRowStyled = styled(TableRow)`	
  		&:nth-of-type(odd) {
    		 background-color:  ${
						enableHeaderColor
							? tableThemes[0].bodyColor
							: tableThemes[1].bodyColor
					};
			 border-radius: 4px;
  		},	
	`;

	const TableHeadStyled = styled(TableHead)`
		& .MuiTableCell-root {
			background-color: ${tableHeaderColumColor} !important;
			height: var(--spacing-32-px, 32px);
			flex-shrink: 0;
		}
	`;
	const handleRequestSort = (
		event: React.MouseEvent<unknown>,
		property: keyof any,
	) => {
		const isAsc = orderBy === property && order === 'asc';
		setOrder(isAsc ? 'desc' : 'asc');
		setOrderBy(property);
		setPage(0);
		setTextFieldPage(1);
		const payload = {
			pageSize: rowsPerPage,
			pageNumber: 0,
			sortKey: property,
			sortDirection: isAsc ? 'desc' : 'asc',
		};
		if (onSortHandler) {
			onSortHandler(payload);
		}
	};

	const getSortDirection = (data: any) =>
		orderBy === data?.id ? order : false;
	const handleChangeRowsPerPage = (
		event: React.ChangeEvent<HTMLInputElement>,
	) => {
		const pagesize = parseInt(event.target.value, 10);
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
		setTextFieldPage(1);
		const pageObj = {
			pageSize: pagesize,
			pageNumber: 0,
			sortKey: orderBy,
			sortDirection: order,
		};
		handlePagination(pageObj);
	};
	const handlePrevPagination = (cPage: number) => {
		setTextFieldPage(cPage - 1);
		setPage(page - 1);
		const pageObj = {
			pageSize: rowsPerPage,
			pageNumber: page - 1,
			sortKey: orderBy,
			sortDirection: order,
		};
		handlePagination(pageObj);
	};
	const handleNextPagination = (cPage: number) => {
		setPage(cPage);
		setTextFieldPage(cPage + 1);
		const pageObj = {
			pageSize: rowsPerPage,
			pageNumber: cPage,
			sortKey: orderBy,
			sortDirection: order,
		};
		handlePagination(pageObj);
	};

	const getHeight = () => {
		const tableHeight = tableStyles?.maxHeight
			? tableStyles?.maxHeight
			: maxHeight;
		return tableHeight;
	};

	const getWidth = () => {
		const tableMinWidth = tableStyles?.minWidth
			? tableStyles?.minWidth
			: minWidth;
		return tableMinWidth;
	};

	const handlePagination = (pageObj: any) => {
		if (onPageHandler) {
			onPageHandler(pageObj, true);
		}
	};
	useEffect(() => {
		setTableData(rows);
	}, [rows]);

	useEffect(() => {
		if (clearSelection) {
			setSelectedIds([]);
			if (isCheckBoxEnabled && onRowChecked) {
				onRowChecked([]);
			}
		}
	}, [clearSelection]);
	const onPageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = parseInt(event.target.value, 10);
		const resultValue = onPageTextChanged(value, rowsPerPage, totalItems);

		const paramData = { pageNumber: resultValue - 1 };
		setPage(resultValue);
		setTextFieldPage(resultValue);
		const payload = {
			pageSize: rowsPerPage,
			pageNumber: paramData?.pageNumber,
			sortKey: orderBy,
			sortDirection: order,
		};
		handlePagination(payload);
	};

	const removeElementFromArray = (element: string, fromArray: string[]) => {
		const selectedIndex = fromArray.indexOf(element);
		let newSelectedArray: readonly any[] = [];
		if (selectedIndex === 0) {
			newSelectedArray = newSelectedArray.concat(fromArray.slice(1));
		} else if (selectedIndex === fromArray.length - 1) {
			newSelectedArray = newSelectedArray.concat(fromArray.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelectedArray = newSelectedArray.concat(
				fromArray.slice(0, selectedIndex),
				fromArray.slice(selectedIndex + 1),
			);
		}
		return newSelectedArray;
	};

	const onCheckBoxSelected = (event: any, data: any) => {
		event.stopPropagation();
		let newSelected: readonly any[] = [];
		if (event.target.checked) {
			newSelected = newSelected.concat(selectedIds, data[rowKey]);
		} else {
			newSelected = removeElementFromArray(data[rowKey], selectedIds);
		}
		setSelectedIds(newSelected);
		if (isCheckBoxEnabled && onRowChecked) {
			onRowChecked(newSelected);
		}
	};
	const onCheckBoxAllSelected = (
		event: React.ChangeEvent<HTMLInputElement>,
		data: any,
	) => {
		let newArrayData = [];
		if (event.target.checked) {
			const tmpIds: string[] = [];
			data.forEach((ele: any) => {
				tmpIds.push(ele[rowKey]);
			});
			tmpIds.push(...selectedIds);
			const uniqArray = tmpIds.filter(
				(item, index) => tmpIds.indexOf(item) === index,
			);
			setSelectedIds(uniqArray);
			newArrayData = uniqArray;
		} else {
			let newSelected = selectedIds;
			/// pop from table
			data.forEach((ele: any) => {
				newSelected = removeElementFromArray(ele[rowKey], newSelected);
			});
			setSelectedIds(newSelected);
			newArrayData = newSelected;
		}
		if (onRowChecked) {
			onRowChecked(newArrayData);
		}
	};

	const onTableRowSelect = (event: any, data: any) => {
		if (!event?.target?.classList?.contains('selectCheckbox')) {
			if (onRowSelectable) {
				onRowSelectable(data);
			}
		}
	};

	const handleShowEndPage = () => {
		if (totalItems > 0) {
			const val = totalItems / rowsPerPage;
			if (val % 1 !== 0) {
				return Math.trunc(val + 1);
			}
			return totalItems / rowsPerPage;
		}
		return totalItems / rowsPerPage;
	};
	const createSortHandler =
		(property: keyof any) => (event: React.MouseEvent<unknown>) => {
			handleRequestSort(event, property);
		};
	useEffect(() => {
		const pageObj = { pageSize: rowsPerPage, pageNumber: page };
		if (onPageHandler) {
			onPageHandler(pageObj, false);
		}
	}, []);

	const setRowHover = (data: string) => {
		if (rowHoverEnabled) {
			setRowHoverData(data);
		}
	};

	const setRowHoverHide = () => {
		if (rowHoverEnabled) {
			setRowHoverData('');
		}
	};

	const getSortKeyDirection = (id: string) => {
		if (orderBy === id) {
			return order;
		}
		return 'asc';
	};

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setTextFieldPage(event.target.value);
		debouncedOnPageSelect(event);
	};

	const debouncedOnPageSelect = debounce(onPageSelect, 500);
	const onInputFocus = () => {
		inputRef?.current?.select();
	};
	return (
		<div className={styles['common-table']} data-testid="main-table-div">
			<Paper sx={{ width: '100%' }} elevation={isFlatStyle ? 0 : 1}>
				<TableContainer className={styles['common-table-container']}>
					{isLoading ? (
						<Table className={styles['common-table-box']}>
							<TableBody>
								<StyledTableRow style={{ background: 'none' }}>
									<StyledTableCell style={{ borderBottom: 'none' }}>
										<Loader show />
									</StyledTableCell>
								</StyledTableRow>
							</TableBody>
						</Table>
					) : (
						<Table
							aria-labelledby="tableTitle"
							stickyHeader
							sx={{
								minWidth: getWidth(),
								...tablePadding,
							}}
						>
							<TableHeadStyled>
								<TableRowStyled>
									{isCheckBoxEnabled && rows.length > 0 && (
										<StyledTableCell
											component="th"
											scope="row"
											key={`enhanced-table-colheader-checkbox-${page}-${isCheckBoxEnabled}`}
											align="left"
											sx={{ width: '50px' }}
											className={styles['table-column-cell-head']}
										>
											<Checkbox
												size="small"
												aria-checked={
													rows.length > 0 && selectedIds.length === rows.length
												}
												data-testid="checkbox-all-selection"
												name="CheckBoxOutlineBlankIcon"
												role="checkbox"
												sx={{
													'& :hover': { background: 'none' },
													[`&, &.${checkboxClasses.checked}`]: {
														color: checkboxSelectionColor,
													},
												}}
												checked={
													rows.length > 0 && selectedIds.length && allSelected
												}
												onChange={(e) => {
													onCheckBoxAllSelected(e, rows);
												}}
											/>
										</StyledTableCell>
									)}
									{columns && columns.length > 0 ? (
										columns.map((headCell: Column) => {
											const colId = `enhanced-table-colheader-${headCell?.id}-${page}`;
											const align = headCell.isAlignRight ? 'right' : 'left';
											return (
												// Use a ternary operator to check the isHidden condition
												headCell.isHidden ? null : ( // If isHidden is true, return null
													<StyledTableCell
														component="th"
														scope="row"
														key={colId}
														align={align}
														sortDirection={getSortDirection(headCell)}
														className={styles['table-column-cell-head']}
													>
														<TableSortLabel
															data-testid="table-sort"
															active={
																headCell?.isSortable || orderBy === headCell?.id
															}
															direction={getSortKeyDirection(headCell.id)}
															onClick={
																headCell?.isSortable
																	? createSortHandler(headCell?.id)
																	: undefined
															}
															className={styles['table-column-cell-head']}
															IconComponent={sortIcon}
															hideSortIcon={!headCell?.isSortable}
															sx={{
																'&:hover': {
																	cursor: !headCell?.isSortable
																		? 'default'
																		: 'pointer',
																},
																'& .MuiIcon-root': {
																	display: 'none',
																},
															}}
														>
															<CellComponent
																headCell={headCell}
																order={order}
																orderBy={orderBy}
																fill={sortIconColor}
															/>
														</TableSortLabel>
													</StyledTableCell>
												)
											);
										})
									) : (
										<StyledTableCell>
											<Paper
												sx={{
													maxHeight: getHeight(),
												}}
												className={styles['common-table-box']}
											>
												No Data Found
											</Paper>
										</StyledTableCell>
									)}
								</TableRowStyled>
							</TableHeadStyled>
							<TableBody
								data-testid="table-data"
								className={styles['table-body-style']}
							>
								{tableData?.length > 0 &&
									tableData?.map((data: any) => {
										const isItemSelected = isSelected(data[rowKey]);
										return (
											<>
												<TableRowStyled
													hover
													tabIndex={-1}
													key={`table-key-${data[rowKey]}-01`}
													data-testid="server-table-rows"
													onMouseOver={() => {
														setRowHover(data[rowKey]);
													}}
													onMouseOut={() => {
														setRowHoverHide();
													}}
													onClick={(event) => {
														onTableRowSelect(event, data);
													}}
												>
													{isCheckBoxEnabled && (
														<StyledTableCell
															scope="row"
															key={`table-key-${data[rowKey]}-${page}}`}
															align="left"
															className={styles['table-column-cell']}
															sx={{ width: '50px' }}
														>
															<Checkbox
																size="small"
																name={data[rowKey]}
																role="checkbox"
																aria-checked={isItemSelected}
																data-testid="servertable-table-checkbox-all-selection"
																inputProps={{ className: 'selectCheckbox' }}
																sx={{
																	'& :hover': { background: 'none' },
																	[`&, &.${checkboxClasses.checked}`]: {
																		color: checkboxSelectionColor,
																	},
																}}
																checked={isItemSelected}
																onChange={(e) => {
																	onCheckBoxSelected(e, data);
																}}
															/>
														</StyledTableCell>
													)}
													<TableRowComponent
														isItemSelected={isItemSelected}
														rowHoverData={rowHoverData}
														rowData={data}
														tableProps={props}
													/>
												</TableRowStyled>

												{enableInnerTableRow && data.expand && (
													<TableRowStyled
														className={
															data.expand
																? styles['inner-table-container']
																: styles['inner-table-displaycontainer']
														}
													>
														<StyledTableCell colSpan={colSpan}>
															{enableInnerTableRow &&
															data.expand &&
															enableInnerTableTemplate
																? enableInnerTableTemplate(data)
																: null}
														</StyledTableCell>
													</TableRowStyled>
												)}
											</>
										);
									})}
							</TableBody>
						</Table>
					)}
					{rows?.length <= 0 && !isLoading && (
						<Paper
							sx={{ maxHeight: getHeight() }}
							className={styles['common-table-box']}
						>
							{customErrorMessage || 'No Data Found'}
						</Paper>
					)}
				</TableContainer>
			</Paper>
			{rows?.length > 0 && isRenderPagination && (
				<Grid container>
					{hideSelectPageOption ? (
						<Grid
							item
							xs={6}
							className={styles['common-table-rows-per-page']}
						/>
					) : (
						<Grid item xs={6} className={styles['common-table-rows-per-page']}>
							<FormControl
								variant="standard"
								sx={{ m: 1, minWidth: 120 }}
								className={styles['common-table-form-control']}
							>
								<span className={styles['common-table-show-text']}>Show</span>
								<Select
									type="listbox"
									data-testid="servertable-pages-select"
									value={rowsPerPage}
									IconComponent={ExpandMoreRoundedIcon}
									onChange={(e: any) => handleChangeRowsPerPage(e)}
									className={styles['common-table-select']}
									id="demo-simple-select-standard"
								>
									<MenuItem value={TABLE_SIZE_10}>10</MenuItem>
									<MenuItem value={TABLE_SIZE_25}>25</MenuItem>
									<MenuItem value={TABLE_SIZE_50}>50</MenuItem>
								</Select>
								<span className={styles['common-table-show-text']}>items</span>
							</FormControl>
						</Grid>
					)}
					{isRenderPagination && (
						<Grid item xs={6} className={styles['common-table-pages-grid']}>
							<IconButton
								aria-label="delete"
								data-testid="servertable-prev-icon"
								onClick={() => {
									handlePrevPagination(page + 1);
								}}
								className={styles['common-table-icon-button-prev']}
								disabled={page === 0}
							>
								{ImgTagComponent(
									prevPaginationIcon,
									'prevPaginationIcon',
									'prev-pagination-styles',
								)}
							</IconButton>
							<TextField
								id="outlined-size-small"
								value={textFieldPage}
								size="small"
								inputRef={inputRef}
								onFocus={onInputFocus}
								className={styles['common-table-pages-grid-input-field']}
								onChange={(e: any) => handleChange(e)}
								inputProps={{
									'data-testid': 'servertable-pagenumber-textfield',
									disabled:
										totalItems <= rowsPerPage || handleShowEndPage() <= 1,
									max: handleShowEndPage(),
								}}
							/>
							of {handleShowEndPage()}
							<IconButton
								aria-label="delete"
								data-testid="servertable-next-icon"
								onClick={() => {
									handleNextPagination(page + 1);
								}}
								className={styles['common-table-icon-button-next']}
								disabled={page + 1 === handleShowEndPage()}
							>
								{ImgTagComponent(
									nextPaginationIcon,
									'nextPaginationIcon',
									'next-pagination-styles',
								)}
							</IconButton>
						</Grid>
					)}
				</Grid>
			)}
		</div>
	);
};

export default TableComponent;
